import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAdmin } from '../../context/AdminContext';
import { useFamily } from '../../context/FamilyContext';
import { HiLogout, HiChartPie, HiChatAlt, HiDeviceMobile } from 'react-icons/hi';
import toast from 'react-hot-toast';
import navItems from '../../utils/navItems';
import { buildFeedbackMailto } from '@coparent/shared/src/utils/feedback';
import Logo from '../common/Logo';

const feedbackHref = buildFeedbackMailto({
  platform: 'Web',
  version: process.env.REACT_APP_VERSION ?? 'web',
});

const Sidebar = () => {
  const { currentUser, logout } = useAuth();
  const { isAdmin } = useAdmin();
  const { family, getFamilyDisplayName, getCurrentParentProfile } = useFamily();
  const navigate = useNavigate();
  const profile = getCurrentParentProfile();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      toast.error('Failed to log out');
    }
  };

  return (
    <aside className="hidden lg:flex lg:flex-col lg:w-72 lg:fixed lg:inset-y-0 bg-white border-r border-surface-200">
      {/* Logo */}
      <div className="px-7 py-6">
        <div className="flex items-center gap-3">
          <Logo size={32} variant="light" />
          <div>
            <h1 className="font-display text-[0.9375rem] font-medium text-canopy tracking-tight">coparent</h1>
            {family && (
              <p className="text-xs text-ink-400 leading-none mt-0.5">{getFamilyDisplayName()}</p>
            )}
          </div>
        </div>
      </div>

      {/* Nav links */}
      <nav className="flex-1 min-h-0 overflow-y-auto px-4 py-2 space-y-0.5">
        {navItems.map(item =>
          item.external ? (
            <a
              key={item.to}
              href={item.to}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[0.8125rem] font-medium transition-all duration-200 text-ink-500 hover:bg-surface-100 hover:text-ink-800"
            >
              <item.icon className="text-[1.125rem]" />
              {item.label}
            </a>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/dashboard'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[0.8125rem] font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-canopy text-white shadow-warm-sm'
                    : 'text-ink-500 hover:bg-surface-100 hover:text-ink-800'
                }`
              }
            >
              <item.icon className="text-[1.125rem]" />
              {item.label}
            </NavLink>
          )
        )}
        {isAdmin && (
          <NavLink
            to="/admin/metrics"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[0.8125rem] font-medium transition-all duration-200 mt-4 border-t border-surface-100 pt-4 ${
                isActive
                  ? 'bg-canopy text-white shadow-warm-sm'
                  : 'text-ink-500 hover:bg-surface-100 hover:text-ink-800'
              }`
            }
          >
            <HiChartPie className="text-[1.125rem]" />
            Admin metrics
          </NavLink>
        )}
      </nav>

      {/* User info */}
      <div className="px-4 py-5 border-t border-surface-100">
        <div className="flex items-center gap-3 px-2 mb-3">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold ring-2 ring-white shadow-warm-sm"
            style={{ backgroundColor: profile?.color || '#1B6B4A' }}
          >
            {currentUser?.displayName?.[0]?.toUpperCase() || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-ink-800 truncate">
              {currentUser?.displayName}
            </p>
            <p className="text-xs text-ink-400 truncate">{currentUser?.email}</p>
          </div>
        </div>
        <a
          href={feedbackHref}
          className="flex items-center gap-2.5 px-3.5 py-2 w-full text-[0.8125rem] text-ink-400 hover:text-canopy hover:bg-surface-100 rounded-xl transition-all duration-200"
        >
          <HiChatAlt className="text-base" />
          Send feedback
        </a>
        <a
          href="https://apps.apple.com/us/app/coparent-co-parenting-hub/id6761960413?utm_source=web&utm_medium=sidebar"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2.5 px-3.5 py-2 w-full text-[0.8125rem] text-ink-400 hover:text-canopy hover:bg-surface-100 rounded-xl transition-all duration-200"
        >
          <HiDeviceMobile className="text-base" />
          Get the iOS app
        </a>
        <a
          href="https://play.google.com/store/apps/details?id=com.letscoparent.app&utm_source=web&utm_medium=sidebar"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2.5 px-3.5 py-2 w-full text-[0.8125rem] text-ink-400 hover:text-canopy hover:bg-surface-100 rounded-xl transition-all duration-200"
        >
          <HiDeviceMobile className="text-base" />
          Get the Android app
        </a>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2.5 px-3.5 py-2 w-full text-[0.8125rem] text-ink-400 hover:text-parent2-600 hover:bg-parent2-50 rounded-xl transition-all duration-200"
        >
          <HiLogout className="text-base" />
          Sign Out
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
